import * as React from 'react';
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Box, Modal, Button, Stack, TextField, Typography,
  Divider, Checkbox, FormControlLabel, Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '45vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  overflowY: 'auto',
  backgroundColor: '#f5f5f5',
};

export default function ModalEmissaoDocumentacao({
  open, onClose, proposta, setProposta, anexo, onAddAnexo, onConfirmEmissao,
}) {
  const dadosIniciais = proposta?.emissaoDoc || {};
  const [possuiDocAprovado, setPossuiDocAprovado] = useState(!!dadosIniciais.possuiDocAprovado);
  const [numeroNS, setNumeroNS] = useState(dadosIniciais.numeroNS || '');
  const [dataEmissao, setDataEmissao] = useState(dadosIniciais.dataEmissao || '');
  const [dataExpiracao, setDataExpiracao] = useState(dadosIniciais.dataExpiracao || '');
  const [valorServico, setValorServico] = useState(proposta?.valorservico ?? '');
  const [servicoInterno, setServicoInterno] = useState(!!proposta?.servicoInterno);

  const qtdDocumentos = React.useMemo(() => anexo?.length || 0, [anexo]);

  const dadosAtuais = () => ({
    possuiDocAprovado,
    numeroNS: numeroNS.trim(),
    dataEmissao,
    dataExpiracao,
    servicoInterno,
    valorservico: servicoInterno ? 0 : parseFloat(valorServico) || 0,
  });

  // Persiste os dados na proposta para não perder ao anexar / reabrir o modal.
  const persistirNaProposta = () => {
    // Merge preserva campos já existentes (ex.: marcador agendaExpiracaoRef) ao atualizar o formulário.
    setProposta((prev) => ({
      ...prev,
      servicoInterno,
      valorservico: servicoInterno ? 0 : parseFloat(valorServico) || 0,
      emissaoDoc: { ...(prev.emissaoDoc || {}), ...dadosAtuais() },
    }));
  };

  const handleAddDocumento = () => {
    persistirNaProposta();
    onAddAnexo();
  };

  // Só exige NS/datas quando o checkbox está marcado.
  const podeSalvar = !possuiDocAprovado || (numeroNS.trim() && dataEmissao && dataExpiracao);

  const handleSalvar = () => {
    onConfirmEmissao(dadosAtuais());
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>
          Emissão de Documentação
        </Typography>

        {/* Serviço executado internamente (engenheiro próprio, não terceirizado) */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={servicoInterno}
                onChange={(e) => setServicoInterno(e.target.checked)}
                color="primary"
              />
            )}
            label={(
              <Typography variant="body2">
                Serviço executado internamente (não é necessário informar valor/NF de terceirização)
              </Typography>
            )}
          />
        </Box>

        {/* Valor do serviço (custo da terceirização) — dispensado quando interno */}
        {!servicoInterno && (
          <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="#1565C0" mb={1}>
              Valor do Serviço (custo da terceirização)
            </Typography>
            <TextField
              size="small"
              label="Valor (R$)"
              type="number"
              value={valorServico}
              onChange={(e) => setValorServico(e.target.value)}
              onBlur={() => setProposta((prev) => ({ ...prev, valorservico: parseFloat(valorServico) || 0 }))}
              sx={{ maxWidth: 220, backgroundColor: 'white' }}
              InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
            />
          </Box>
        )}

        {/* Documentos do serviço */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="#1565C0" mb={1}>
            Documentos do serviço
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            {servicoInterno
              ? 'Serviço interno: anexo de documentação e Nota Fiscal de terceirização não são necessários (opcional).'
              : 'Anexe aqui os documentos elaborados e a Nota Fiscal do serviço (selecione o tipo ao anexar). Ficam disponíveis na aba Anexos.'}
          </Typography>
          <Stack direction="row" alignItems="center" gap={2}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleAddDocumento}
              sx={{ borderColor: '#1565C0', color: '#1565C0' }}
            >
              Adicionar Documento
            </Button>
            <Chip
              icon={<DescriptionIcon />}
              label={`${qtdDocumentos} anexo(s)`}
              size="small"
              color={qtdDocumentos > 0 ? 'success' : 'default'}
            />
          </Stack>
        </Box>

        {/* Documento aprovado por distribuidora */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={possuiDocAprovado}
                onChange={(e) => setPossuiDocAprovado(e.target.checked)}
                color="warning"
              />
            }
            label={(
              <Typography variant="body2">
                Possui documento aprovado por distribuidora
              </Typography>
            )}
          />

          {possuiDocAprovado && (
            <Stack gap={2} sx={{ mt: 1 }}>
              <TextField
                size="small"
                label="Número da NS"
                value={numeroNS}
                onChange={(e) => setNumeroNS(e.target.value)}
                sx={{ maxWidth: 260, backgroundColor: 'white' }}
              />
              <Stack direction="row" gap={2}>
                <TextField
                  size="small"
                  label="Data da Emissão"
                  type="date"
                  value={dataEmissao}
                  onChange={(e) => setDataEmissao(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ maxWidth: 200, backgroundColor: 'white' }}
                />
                <TextField
                  size="small"
                  label="Data da Expiração"
                  type="date"
                  value={dataExpiracao}
                  onChange={(e) => setDataExpiracao(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ maxWidth: 200, backgroundColor: 'white' }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Ao salvar, os dados da NS são registrados nas Atividades e uma agenda é criada na data de expiração.
              </Typography>
            </Stack>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" color="error" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSalvar}
            disabled={!podeSalvar}
          >
            Salvar
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

ModalEmissaoDocumentacao.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  proposta: PropTypes.object,
  setProposta: PropTypes.func,
  anexo: PropTypes.array,
  onAddAnexo: PropTypes.func,
  onConfirmEmissao: PropTypes.func,
};